import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAnalystReport } from '@/hooks/useAnalystReports';
import { usePageTitle } from '@/hooks/usePageTitle';
import { generateAnalystReportPDF } from '@/lib/analyst-report-pdf';
import { formatDateTime } from '@/utils/format';

import { StatusPill } from './StatusPill';

import type { AnalystBreakdownRow, AnalystStats } from '@repo/shared-types';

export const AnalystReportDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: report, isLoading } = useAnalystReport(id);
  usePageTitle(report?.title ?? 'AI Analyst');
  const { toast } = useToast();

  if (isLoading || !report) {
    return (
      <div className="text-muted-foreground container mx-auto py-10 text-center">Loading…</div>
    );
  }

  const busy = report.status === 'PENDING' || report.status === 'GENERATING';
  const { narrative, stats } = report;

  return (
    <div className="container mx-auto max-w-4xl space-y-6 py-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/ai-analyst"
            className="text-muted-foreground mb-2 inline-flex items-center text-sm hover:underline"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            All reports
          </Link>
          <h1 className="text-3xl font-bold">{report.title}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {formatDateTime(report.created_at)}
            {report.llm_provider ? ` • ${report.llm_provider}` : ''}
            {report.llm_model ? ` (${report.llm_model})` : ''}
            {report.params?.sample_size ? ` • ${report.params.sample_size} transcripts` : ''}
            {report.params?.configuration_label
              ? ` • config: ${report.params.configuration_label}`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill status={report.status} />
          {report.status === 'COMPLETED' && (
            <Button
              onClick={() => {
                generateAnalystReportPDF(report);
                toast({ variant: 'success', title: 'PDF downloaded' });
              }}
            >
              <Download className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
          )}
        </div>
      </div>

      {busy && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
            <p className="font-medium">The AI Analyst is reading the calls…</p>
            <p className="text-muted-foreground text-sm">
              Computing stats and coding transcript patterns. This page updates automatically.
            </p>
          </CardContent>
        </Card>
      )}

      {report.status === 'FAILED' && (
        <Card className="border-red-300">
          <CardHeader>
            <CardTitle className="text-red-700">Generation failed</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-700">{report.error_message ?? 'Unknown error.'}</p>
          </CardContent>
        </Card>
      )}

      {report.status === 'COMPLETED' && narrative && (
        <>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <p className="text-xl font-semibold">{narrative.headline}</p>
              {narrative.executive_summary.map((para, i) => (
                <p key={i} className="text-muted-foreground leading-relaxed">
                  {para}
                </p>
              ))}
            </CardContent>
          </Card>

          {stats && <StatsSection stats={stats} />}

          <Card>
            <CardHeader>
              <CardTitle>Key findings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {narrative.key_findings.map((f, i) => (
                <div key={i}>
                  <p className="font-medium">
                    {i + 1}. {f.title}
                  </p>
                  <p className="text-muted-foreground text-sm">{f.detail}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {narrative.reason_breakdown.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Why the failure group didn’t convert</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b text-left">
                      <th className="py-2">Reason</th>
                      <th className="py-2 text-right">Count</th>
                      <th className="py-2 text-right">Share</th>
                      <th className="py-2 pl-4">Nature</th>
                    </tr>
                  </thead>
                  <tbody>
                    {narrative.reason_breakdown.map((r, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-4">{r.reason}</td>
                        <td className="py-2 text-right tabular-nums">{r.count}</td>
                        <td className="py-2 text-right tabular-nums">{r.share_pct}%</td>
                        <td className="text-muted-foreground py-2 pl-4">{r.nature}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Recommendations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[...narrative.recommendations]
                .sort((a, b) => a.priority - b.priority)
                .map((r, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="bg-primary/10 text-primary mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold">
                      {r.priority}
                    </span>
                    <div>
                      <p className="font-medium">{r.title}</p>
                      <p className="text-muted-foreground text-sm">{r.detail}</p>
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>

          <Card className="bg-muted/40">
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
                Bottom line
              </p>
              <p className="mt-2 leading-relaxed">{narrative.bottom_line}</p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

function StatsSection({ stats }: { stats: AnalystStats }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>The numbers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label={stats.success_label} value={stats.success_count} />
          <Stat label={stats.failure_label} value={stats.failure_count} />
          <Stat label="Total" value={stats.total} />
          <Stat label="Success share" value={`${stats.success_share_pct}%`} />
        </div>

        <BreakdownTable title="Conversion by channel" rows={stats.by_channel} />
        <BreakdownTable title="Conversion by configuration" rows={stats.by_configuration} />

        {stats.failure_status_breakdown.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-medium">Failure-group payment status</p>
            <div className="flex flex-wrap gap-2">
              {stats.failure_status_breakdown.map((s) => (
                <span key={s.label} className="bg-muted rounded-md px-2.5 py-1 text-xs">
                  {s.label}: <span className="font-semibold">{s.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-muted-foreground text-xs">{label}</p>
    </div>
  );
}

function BreakdownTable({ title, rows }: { title: string; rows: AnalystBreakdownRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <p className="mb-2 text-sm font-medium">{title}</p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3 text-sm">
            <span className="w-40 shrink-0 truncate">{r.label}</span>
            <div className="bg-muted h-4 flex-1 overflow-hidden rounded">
              <div
                className="bg-primary/70 h-full rounded"
                style={{ width: `${Math.min(r.conversion_pct, 100)}%` }}
              />
            </div>
            <span className="text-muted-foreground w-28 shrink-0 text-right tabular-nums">
              {r.success}/{r.total} • {r.conversion_pct}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
